import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Modal, Radio, Select, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import DocumentTemplateEditor from '@/components/tiptap-editor/DocumentTemplateEditor';
import TokenChips from '@/components/tiptap-editor/TokenChips';
import {
  createEmailTemplate,
  getEmailTemplateTokens,
  previewEmailTemplateDraft,
  updateEmailTemplate,
} from '@/services/emailTemplates.service';
import { EMAIL_TEMPLATE_EVENTS, EMAIL_TEMPLATES_QUERY_KEY } from './constants';
import EmailPreviewFrame from './EmailPreviewFrame';
import useEmployeeNoticesT, { errorText } from './useEmployeeNoticesT';

const RECIPIENT_MODES = ['allEmployees', 'sector', 'addresses'];
const LANGUAGES = ['sr', 'en'];
// Shown literally in the subject hint (a token is typed there by hand).
const SAMPLE_TOKEN = '{{employeeFullName}}';

const EMPTY = {
  name: '',
  event: 'newEmployee',
  subject: '',
  bodyHtml: '',
  recipientsMode: 'allEmployees',
  addresses: [],
  language: 'sr',
};

/**
 * Create or edit a company email template: subject and body with the same
 * tokens as custom documents (plus the employee photo), who receives it and
 * in which language the frame is.
 */
export default function EmailTemplateModal({ open, onClose, companyId, template }) {
  const { t, tn } = useEmployeeNoticesT();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const mode = Form.useWatch('recipientsMode', form);
  const [preview, setPreview] = useState(null);

  const { data: tokens = [] } = useQuery({
    queryKey: [EMAIL_TEMPLATES_QUERY_KEY, 'tokens'],
    queryFn: getEmailTemplateTokens,
    staleTime: Infinity,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ ...EMPTY, ...(template || {}) });
  }, [open, template, form]);

  const save = useMutation({
    mutationFn: (values) =>
      template?._id
        ? updateEmailTemplate(template._id, values)
        : createEmailTemplate({ ...values, company: companyId }),
    onSuccess: () => {
      message.success(tn('templateSaved'));
      queryClient.invalidateQueries({ queryKey: [EMAIL_TEMPLATES_QUERY_KEY, companyId] });
      onClose();
    },
    onError: (error) => message.error(errorText(error, tn('saveFailed'))),
  });

  // The fields as they stand (saved or not), filled for a sample employee.
  const previewDraft = useMutation({
    mutationFn: () => {
      const { subject, bodyHtml, language } = form.getFieldsValue();
      return previewEmailTemplateDraft({
        company: companyId || template?.company,
        subject,
        bodyHtml,
        language,
      });
    },
    onSuccess: setPreview,
    onError: (error) => message.error(errorText(error, tn('previewFailed'))),
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={880}
      destroyOnHidden
      title={template?._id ? tn('editTemplate') : tn('newTemplate')}
      okText={t('save', { ns: 'forms' })}
      cancelText={t('cancel', { ns: 'forms' })}
      okButtonProps={{ loading: save.isPending, className: '!rounded-[50px]' }}
      cancelButtonProps={{ className: '!rounded-[50px]' }}
      onOk={() => form.validateFields().then((values) => save.mutate(values))}
      footer={(_, { OkBtn, CancelBtn }) => (
        <div className="flex flex-wrap justify-between gap-2">
          <Button
            className="!rounded-[50px]"
            icon={<EyeOutlined />}
            loading={previewDraft.isPending}
            onClick={() => previewDraft.mutate()}
          >
            {tn('previewEmail')}
          </Button>
          <div className="flex gap-2">
            <CancelBtn />
            <OkBtn />
          </div>
        </div>
      )}
    >
      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        width={760}
        destroyOnHidden
        title={tn('previewEmail')}
        footer={
          <Button className="!rounded-[50px]" onClick={() => setPreview(null)}>
            {t('close', { ns: 'forms' })}
          </Button>
        }
      >
        {preview && (
          <div className="grid gap-2">
            <p className="m-0 text-[12.5px] text-zinc-500">
              {tn('previewSample', { name: preview.sampleEmployee?.name || '—' })}
            </p>
            <EmailPreviewFrame subject={preview.subject} html={preview.html} height={560} />
          </div>
        )}
      </Modal>
      <Form form={form} layout="vertical" initialValues={EMPTY} autoComplete="off">
        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <Form.Item
            name="name"
            label={tn('templateName')}
            rules={[{ required: true, whitespace: true, message: tn('templateNameRequired') }]}
          >
            <Input placeholder={tn('templateNamePlaceholder')} />
          </Form.Item>
          <Form.Item name="event" label={tn('event')}>
            <Select
              options={EMAIL_TEMPLATE_EVENTS.map((key) => ({
                value: key,
                label: tn(`events.${key}`),
              }))}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="subject"
          label={tn('subject')}
          rules={[{ required: true, whitespace: true, message: tn('subjectRequired') }]}
          className="!mb-2"
        >
          <Input placeholder={tn('subjectPlaceholder', { token: SAMPLE_TOKEN })} />
        </Form.Item>
        {/* Click a token to add it to the subject (no photo in a subject). */}
        <div className="mb-5">
          <div className="mb-1 text-xs text-zinc-500">{tn('subjectTokensHint')}</div>
          <TokenChips
            tokens={tokens.filter(({ token }) => token !== 'employeePhoto')}
            onInsert={(token) =>
              form.setFieldValue(
                'subject',
                `${form.getFieldValue('subject') || ''}{{${token}}}`
              )
            }
          />
        </div>

        <Form.Item name="bodyHtml" label={tn('body')} extra={tn('bodyHint')}>
          <DocumentTemplateEditor
            tokens={tokens}
            showSignature={false}
            placeholder={tn('bodyPlaceholder')}
          />
        </Form.Item>

        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-[minmax(0,1fr)_200px]">
          <Form.Item name="recipientsMode" label={tn('recipients')}>
            <Radio.Group
              options={RECIPIENT_MODES.map((key) => ({
                value: key,
                label: tn(`recipientsModes.${key}`),
              }))}
            />
          </Form.Item>
          <Form.Item name="language" label={tn('language')} extra={tn('languageHint')}>
            <Select
              options={LANGUAGES.map((key) => ({ value: key, label: tn(`languages.${key}`) }))}
            />
          </Form.Item>
        </div>

        {mode === 'addresses' && (
          <Form.Item
            name="addresses"
            label={tn('addresses')}
            extra={tn('addressesHint')}
            rules={[
              {
                validator: (_, value) =>
                  value?.length
                    ? Promise.resolve()
                    : Promise.reject(new Error(tn('addressesRequired'))),
              },
            ]}
          >
            <Select mode="tags" tokenSeparators={[',', ';', ' ']} open={false} suffixIcon={null} />
          </Form.Item>
        )}

        <Alert type="info" showIcon className="!mt-1" message={tn('oneEmailInfo')} />
      </Form>
    </Modal>
  );
}
